import { Link } from "@tanstack/react-router";

import { FEES, FIRM } from "@/lib/firm";
import { relatedGuides, type Guide } from "@/lib/guides";

type ServiceLink = { to: "/conveyancing" | "/wills-and-estates" | "/elder-law" | "/business-lawyers"; label: string; blurb: string };

const SERVICE_BY_CATEGORY: Record<Guide["category"], ServiceLink> = {
  Conveyancing: {
    to: "/conveyancing",
    label: "Fixed-fee conveyancing",
    blurb: `Buying or selling on the Sunshine Coast — ${FEES.sale} to sell, ${FEES.purchase} to buy, both incl. GST.`,
  },
  "Wills & Estates": {
    to: "/wills-and-estates",
    label: "Wills, probate and estates",
    blurb: "Wills, executor work, probate applications and estate administration in Queensland.",
  },
  "Elder Law": {
    to: "/elder-law",
    label: "Elder law",
    blurb:
      "Powers of attorney, advance health directives, granny flats, retirement villages and aged care.",
  },
  "Business & Commercial": {
    to: "/business-lawyers",
    label: "Property and commercial",
    blurb: "Commercial property, business sales and purchases, and management rights.",
  },
};

const TOPIC_LINKS: Record<string, { to: string; label: string }[]> = {
  "probate-notices-queensland": [
    { to: "/probate-queensland", label: "Help with the notice, grant and deceased estate administration" },
    { to: "/guides/executor-duties-in-queensland", label: "Executor responsibilities after the grant" },
    { to: "/probate-enquiry", label: "Ask what this estate needs next" },
  ],
  "how-to-apply-for-probate-in-queensland": [
    { to: "/probate-queensland", label: "Solicitor assistance with your Queensland probate application" },
    { to: "/probate-enquiry", label: "Request a quote for the grant or full estate administration" },
  ],
  "executor-duties-in-queensland": [
    { to: "/probate-queensland", label: "Deceased estate administration: assets, debts and distributions" },
    { to: "/probate-enquiry", label: "Talk through your executor responsibilities" },
  ],
  "when-to-update-your-will-queensland": [
    { to: "/wills-estate-lawyers-sunshine-coast", label: "Wills and estate planning near Caloundra" },
    { to: "/fixed-price-will-solicitor", label: "Solicitor-drafted wills and starting fees" },
  ],
  "what-happens-if-you-die-without-a-will-in-queensland": [
    { to: "/fixed-price-will-solicitor", label: "Make a will and choose your executor" },
    { to: "/probate-enquiry", label: "Someone has died without a will? Ask about administering the estate" },
  ],
  "how-much-does-conveyancing-cost-in-queensland": [
    { to: "/conveyancing-cost-qld", label: "Conveyancing cost overview" },
    { to: "/fees", label: "Our fee schedule and third-party costs" },
    { to: "/contact", label: "Request a written property quote" },
    { to: "/conveyancing-sunshine-coast", label: "Sunshine Coast conveyancing and contract review" },
    { to: "/conveyancing/redcliffe", label: "Redcliffe Peninsula conveyancing" },
  ],
  "cooling-off-period-queensland": [
    { to: "/cooling-off-period-qld", label: "Cooling-off rights, notice and penalty" },
    { to: "/buyers-solicitor-qld", label: "Ask a solicitor to review your contract" },
  ],
  "joint-tenants-vs-tenants-in-common-queensland": [
    { to: "/guides/title-search-queensland", label: "Check your current title" },
    { to: "/transfer-property-to-family-qld", label: "Changing owners or shares" },
    { to: "/wills-and-estates", label: "Review your will alongside the title" },
  ],
  "due-diligence-clause-queensland": [
    { to: "/buyers-solicitor-qld", label: "Residential buyer checks and contract review" },
    { to: "/commercial-conveyancing", label: "Commercial property due diligence" },
    { to: "/special-conditions-contract-qld", label: "Special conditions for your purchase" },
  ],
  "title-search-queensland": [
    { to: "/guides/easements-in-queensland", label: "Read an easement's terms" },
    { to: "/buyers-solicitor-qld", label: "Get advice on your title and contract" },
  ],
  "easements-in-queensland": [
    { to: "/guides/title-search-queensland", label: "Title searches and registered plans" },
    { to: "/buyers-solicitor-qld", label: "Check access and easements before buying" },
  ],
  "pre-settlement-inspection-checklist-queensland": [
    { to: "/buyers-solicitor-qld", label: "Fixtures, chattels and buyer advice" },
  ],
  "how-much-does-probate-cost-in-queensland": [
    { to: "/probate-queensland", label: "Grant-only and estate administration fees" },
    { to: "/probate-enquiry", label: "Ask about the estate and get a quote" },
  ],
  "do-i-need-probate-in-queensland": [
    { to: "/probate-queensland", label: "Probate and letters of administration" },
    { to: "/probate-enquiry", label: "Check whether this estate needs a grant" },
  ],
  "how-long-does-probate-take-in-queensland": [
    { to: "/probate-checklist-queensland", label: "Executor checklist: gather documents for the application" },
    { to: "/probate-enquiry", label: "Ask about the next steps for this estate" },
  ],
  "enduring-power-of-attorney-qld": [
    { to: "/guides/why-a-solicitor-should-prepare-your-enduring-power-of-attorney-qld", label: "General versus enduring powers, and choosing the right arrangement" },
    { to: "/fees", label: "Our enduring power of attorney fees" },
    { to: "/contact", label: "Arrange an appointment to prepare your EPOA" },
  ],
  "diy-will-kit-vs-solicitor-drafted-will-queensland": [
    { to: "/fixed-price-will-solicitor", label: "Standard solicitor-drafted wills and starting fees" },
    { to: "/testamentary-trusts", label: "When a testamentary trust may be appropriate" },
    { to: "/contact", label: "Ask for a will quote for your circumstances" },
  ],
  "commercial-property-conveyancing-queensland": [
    { to: "/commercial-conveyancing", label: "Commercial property advice and fixed-fee quote" },
    { to: "/retail-shop-lease-faqs", label: "Lease review and Form 12 questions" },
  ],
  "how-much-does-it-cost-to-contest-a-will-queensland": [
    { to: "/cost-to-contest-a-will", label: "Legal fees by stage and who may pay" },
    { to: "/estate-disputes", label: "Advice on a family provision claim or will challenge" },
    { to: "/guides/time-limits-contesting-a-will-queensland", label: "Check the time limits before waiting" },
  ],
  "contesting-a-will-in-queensland": [
    { to: "/cost-to-contest-a-will", label: "Understand the potential costs before proceeding" },
    { to: "/estate-disputes", label: "Ask about your circumstances" },
  ],
  "estate-contested-executor-guide-queensland": [
    { to: "/cost-to-contest-a-will", label: "Defence costs and the executor's potential exposure" },
    { to: "/estate-disputes", label: "Advice for an executor responding to a claim" },
  ],
  "seller-disclosure-form-2-queensland-one-year-on": [
    { to: "/seller-disclosure", label: "Order a solicitor-prepared Form 2 disclosure pack" },
    { to: "/selling", label: "Plan the disclosure and sale conveyancing together" },
  ],
};

/** Questions people commonly ask next, per category, each pointing at a real page. */
const NEXT_QUESTIONS: Record<
  Guide["category"],
  ReadonlyArray<{ q: string; slug: string }>
> = {
  Conveyancing: [
    { q: "How much does conveyancing cost in Queensland?", slug: "how-much-does-conveyancing-cost-in-queensland" },
    { q: "How long is the cooling-off period in Queensland?", slug: "cooling-off-period-queensland" },
    { q: "What if the building and pest report is bad?", slug: "building-and-pest-report-came-back-bad" },
    { q: "What should I know before buying from a mortgagee in possession?", slug: "buying-from-a-mortgagee-in-possession-queensland" },
  ],
  "Wills & Estates": [
    { q: "Do I need probate in Queensland?", slug: "do-i-need-probate-in-queensland" },
    { q: "Can someone contest a will in Queensland?", slug: "contesting-a-will-in-queensland" },
    { q: "When should I update my will?", slug: "when-to-update-your-will-queensland" },
  ],
  "Elder Law": [
    { q: "What if my children don't want the family home sold?", slug: "children-trying-to-stop-parents-selling-the-family-home-queensland" },
    { q: "What is an enduring power of attorney?", slug: "enduring-power-of-attorney-qld" },
    { q: "What are an attorney's legal duties?", slug: "attorney-duties-enduring-power-of-attorney-qld" },
    { q: "What should I check before buying into a retirement village?", slug: "buying-into-a-retirement-village-in-queensland" },
    { q: "What are the risks of a reverse mortgage?", slug: "reverse-mortgages-queensland-risks-and-alternatives" },
  ],

  "Business & Commercial": [
    { q: "What do I need to know when buying or selling a business?", slug: "buying-or-selling-a-business-in-queensland" },
    { q: "What are the steps in a business sale, start to finish?", slug: "business-sale-step-by-step-queensland" },
    { q: "How do management rights sales work?", slug: "buying-and-selling-body-corporate-management-rights" },
    { q: "What is different about commercial conveyancing?", slug: "commercial-property-conveyancing-queensland" },
    { q: "What are real estate agencies getting wrong about AML tranche 2?", slug: "aml-tranche-2-real-estate-agents-what-agencies-are-getting-wrong" },
  ],
};

export function GuideRelatedAndNextSteps({ guide }: { guide: Guide }) {
  const service = SERVICE_BY_CATEGORY[guide.category];
  const related = relatedGuides(guide.slug, 3);
  const questions = NEXT_QUESTIONS[guide.category].filter((q) => q.slug !== guide.slug).slice(0, 3);

  return (
    <section className="mt-16 border-t border-border pt-12">
      {TOPIC_LINKS[guide.slug] && (
        <div className="mb-10">
          <h2 className="font-serif text-2xl text-foreground">Advice for your situation</h2>
          <ul className="mt-4 space-y-3">
            {TOPIC_LINKS[guide.slug]?.map((link) => (
              <li key={link.to}><Link to={link.to} className="text-base font-semibold text-primary underline">{link.label}</Link></li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <h2 className="font-serif text-2xl text-foreground">Related questions</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The questions people usually ask next on this topic.
          </p>
          <ul className="mt-5 space-y-3">
            {questions.map((item) => (
              <li key={item.slug}>
                <Link
                  to="/guides/$slug"
                  params={{ slug: item.slug }}
                  className="group flex gap-3 rounded-sm border border-border bg-card p-4 transition-colors hover:bg-secondary/60"
                >
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span className="text-sm font-medium leading-relaxed text-foreground group-hover:underline">
                    {item.q}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {related.length > 0 && (
            <>
              <h3 className="mt-8 text-xs font-semibold uppercase tracking-widest text-accent-foreground/70">
                Keep reading
              </h3>
              <ul className="mt-3 space-y-2">
                {related.map((g) => (
                  <li key={g.slug}>
                    <Link
                      to="/guides/$slug"
                      params={{ slug: g.slug }}
                      className="text-sm leading-relaxed text-muted-foreground underline decoration-border underline-offset-4 hover:text-foreground"
                    >
                      {g.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div>
          <h2 className="font-serif text-2xl text-foreground">Next steps</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Where to go from here if this is your situation.
          </p>
          <ol className="mt-5 space-y-3">
            <NextStep
              step="1"
              title={service.label}
              body={service.blurb}
              link={<Link to={service.to} className="text-sm font-semibold text-primary underline">See how we help</Link>}
            />
            <NextStep
              step="2"
              title="Check the fixed fee"
              body="Our published fees, what is included, and which third-party costs are additional. More complex matters are quoted before we start."
              link={<Link to="/fees" className="text-sm font-semibold text-primary underline">View our fees</Link>}
            />
            <NextStep
              step="3"
              title="Talk it through"
              body={`A short, no-obligation conversation with a lawyer at our ${FIRM.suburb} office.`}
              link={
                <span className="flex flex-wrap gap-4">
                  <a href="#consultation" className="text-sm font-semibold text-primary underline">
                    Request a consultation
                  </a>
                  <a href={FIRM.phoneHref} className="text-sm font-semibold text-primary underline">
                    Call {FIRM.phone}
                  </a>
                </span>
              }
            />
          </ol>

          <p className="mt-6 text-sm text-muted-foreground">
            Prefer to browse first?{" "}
            <Link to="/guides" className="font-semibold text-foreground underline">
              All legal guides
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}

function NextStep({
  step,
  title,
  body,
  link,
}: {
  step: string;
  title: string;
  body: string;
  link: React.ReactNode;
}) {
  return (
    <li className="flex gap-4 rounded-sm border border-border bg-card p-5">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
        {step}
      </span>
      <span className="block">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">{body}</span>
        <span className="mt-2 block">{link}</span>
      </span>
    </li>
  );
}
